import { useCallback } from 'react';
import { get } from 'lodash';
import { errorAlertObject } from '../../../form/FormConstants';
import { HTTP_STATUS_400 } from '../../../../services/ApiDataService';
import useUiAlerts, { createAlertConfig, ERROR } from '../../../../stores/AlertsStore';

/**
 * Custom hook for handling infrastructure action errors with consistent alert creation.
 *
 * Distinguishes between validation errors (HTTP 400) and generic server errors,
 * providing appropriate user feedback through the alert system.
 *
 * @returns {Function} Error handler callback that creates alerts based on error type
 *
 * @example
 * function MyComponent() {
 *   const errorHandler = useInfrastructureErrorHandler();
 *
 *   const handleAction = async () => {
 *     try {
 *       await someApiCall();
 *     } catch (error) {
 *       errorHandler(error);
 *     }
 *   };
 * }
 */
export function useInfrastructureErrorHandler() {
    const [, uiAlertActions] = useUiAlerts();

    return useCallback(
        (errorObject) => {
            const isValidationError = errorObject.getStatus?.() === HTTP_STATUS_400;
            const alertConfig = isValidationError
                ? createAlertConfig(ERROR, get(errorObject.responseBody || errorObject, 'message'))
                : errorAlertObject;
            return uiAlertActions.createAlert(alertConfig);
        },
        [uiAlertActions]
    );
}
